import { Row, Col, Menu, Card, Breadcrumb } from "antd";
import {
  UserOutlined,
  SettingOutlined,
  ContactsOutlined,
  BellOutlined,
  LockOutlined,
  MoreOutlined,
} from "@ant-design/icons";
import lang from "src/lang";
import { useEffect, useState } from "react";
import ProfileSettings from "./tabs/ProfileSettings";
import AccountSettings from "./tabs/AccountSettings";
import ContactAndAddress from "./tabs/ContactAndAddress";
import NotificationPreferences from "./tabs/NotificationPreferences";
import AccessPermissions from "./tabs/AccessPermissions";
import OtherSettings from "./tabs/OtherSettings";
import { useNavigate, useParams } from "react-router-dom";
import coreFunctions from "src/common/utils/coreFunctions";

const menuItems = [
  {
    key: "profile_settings",
    label: "Profile Setting",
    icon: <UserOutlined />,
  },
  {
    key: "account_settings",
    label: "Account Settings",
    icon: <SettingOutlined />,
  },
  {
    key: "contact_address",
    label: "Contact & Address",
    icon: <ContactsOutlined />,
  },
  {
    key: "notification_preferences",
    label: "Notification Preferences",
    icon: <BellOutlined />,
  },
  // {
  //   key: "access_permissions",
  //   label: "Access & Permissions",
  //   icon: <LockOutlined />,
  // },
  {
    key: "other_settings",
    label: "Other Settings",
    icon: <MoreOutlined />,
  },
];

const ProfileEdit: React.FC = () => {
  const { tab } = useParams();
  const navigate = useNavigate();

  const [selectedMenuItem, setSelectedMenuItem] =
    useState<string>("profile_settings");

  useEffect(() => {
    if (tab) {
      setSelectedMenuItem(tab);
    }
  }, [tab]);

  useEffect(() => {
    if (selectedMenuItem) {
      navigate(`/user/profile/${selectedMenuItem}`);
    }
  }, [selectedMenuItem]);

  return (
    <div className="profile-edit">
      <style>
        {`
          .profile-edit {
            padding: 20px;
            background-color: #f5f7fa;
            min-height: 100vh;
          }

          .profile-edit .ant-breadcrumb {
            margin-bottom: 16px;
          }

          .profile-edit .ant-card {
            width: 100%;
            min-height: 400px;
          }

          @media (max-width: 767px) {
            .profile-edit {
              padding: 10px;
            }
            .profile-edit .ant-breadcrumb {
              margin-bottom: 8px;
            }
            .profile-edit .ant-card {
              min-height: 300px;
            }
          }

          @media (min-width: 768px) and (max-width: 991px) {
            .profile-edit {
              padding: 15px;
            }
            .profile-edit .ant-breadcrumb {
              margin-bottom: 12px;
            }
            .profile-edit .ant-card {
              min-height: 350px;
            }
          }
        `}
      </style>
      <Breadcrumb separator=">">
        <Breadcrumb.Item>{lang.get("breadcrumbs", ["Home"])}</Breadcrumb.Item>
        <Breadcrumb.Item>
          {lang.get("breadcrumbs", ["Profile"])}
        </Breadcrumb.Item>
        <Breadcrumb.Item>
          {lang.get("breadcrumbs", ["Edit Profile"])}
        </Breadcrumb.Item>
      </Breadcrumb>
      <Row gutter={[16, 16]} style={{ alignItems: "stretch" }}>
        <Col
          xs={24}
          sm={24}
          md={6}
          lg={5}
          xl={4}
          style={{
            marginBottom: 16,
          }}
        >
          <Menu
            mode={coreFunctions.isMobileDevice() ? "horizontal" : "vertical"}
            selectedKeys={[selectedMenuItem]}
            onClick={(e) => setSelectedMenuItem(e.key as string)}
            items={menuItems.map((item) => ({
              ...item,
              style: { marginBottom: "10px" },
            }))}
            style={{ width: "100%", minWidth: 0 }}
          />
        </Col>
        <Col
          xs={24}
          sm={24}
          md={18}
          lg={19}
          xl={20}
          style={{
            flexGrow: 1,
            minWidth: 0,
          }}
        >
          {selectedMenuItem === "profile_settings" && <ProfileSettingTab />}
          {selectedMenuItem === "account_settings" && <AccountSettingsTab />}
          {selectedMenuItem === "contact_address" && <ContactAddressTab />}
          {selectedMenuItem === "notification_preferences" && (
            <NotificationPreferencesTab />
          )}
          {selectedMenuItem === "access_permissions" && (
            <AccessPermissionsTab />
          )}
          {selectedMenuItem === "other_settings" && <OtherSettingsTab />}
        </Col>
      </Row>
    </div>
  );
};

export default ProfileEdit;

/* Profile Setting Tab */
const ProfileSettingTab: React.FC = () => {
  return (
    <Card>
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <ProfileSettings />
        </Col>
      </Row>
    </Card>
  );
};

/* Account Settings Tab */
const AccountSettingsTab: React.FC = () => {
  return (
    <Card>
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <AccountSettings />
        </Col>
      </Row>
    </Card>
  );
};

/* Contact & Address Tab */
const ContactAddressTab: React.FC = () => {
  return (
    <Card>
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <ContactAndAddress />
        </Col>
      </Row>
    </Card>
  );
};

/* Notification Preferences Tab */
const NotificationPreferencesTab: React.FC = () => {
  return (
    <Card>
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <NotificationPreferences />
        </Col>
      </Row>
    </Card>
  );
};

/* Access & Permissions Tab (View Only) */
const AccessPermissionsTab: React.FC = () => {
  return (
    <Card>
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <AccessPermissions />
        </Col>
      </Row>
    </Card>
  );
};

/* Other Settings Tab */
const OtherSettingsTab: React.FC = () => {
  return (
    <Card>
      <Row gutter={[16, 0]}>
        <Col xs={24}>
          <OtherSettings />
        </Col>
      </Row>
    </Card>
  );
};
